/**
 * team-config — the section taxonomy for `UnifiedTeamSection`.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * WHY THIS IS A SEPARATE FILE
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * The section list is the one thing a reviewer or product owner will want to change without
 * reading the whole component. Keeping it here also makes the "every section's roleKey is a real
 * backend value" invariant testable in isolation — see `UnifiedTeamSection.test.tsx`.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * THE VOCABULARY IS NOT DEFINED HERE — IT IS IMPORTED
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * Every `roleKey` below MUST be a member of `PROJECT_ROLE_VALUES`, which re-exports
 * `packages/shared/constants/project-roles.ts` — the single source of truth, mirrored into the
 * `project_role_catalog` table by migration V043 (F). Per `docs/code-structure.md` §4, never
 * hardcode a role string. A `roleKey` that is not in the backend enum would render an Add button
 * that posts a value the Zod enum rejects with a 400.
 *
 * The UI design doc (§5) listed `Leadership` and `Executive` as **BLOCKED** pending backend
 * support (gaps BE-1/BE-2). That block is **lifted**: V043 (F) seeds both into
 * `project_role_catalog` with `is_named_lead = true`, and both are in `PROJECT_ROLE_VALUES`
 * (CR §8.3). They are therefore full sections here.
 *
 * T-29 / T-31 (guarded team changes): Leadership and Account Executive have NO add action on the
 * Team tab (set at intake, changed via Swap); SA / PM / Implementation Lead show an "Assign" action
 * only while empty; Engineers / Other keep "Add" behind a confirmation. See `addMode`.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * NO CAPACITY (out of MVP scope — Task #10)
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * Sections no longer drive any capacity input: every Add dialog posts `capacity_pct: null`. The
 * former `sectionRequiresCapacity()` helper was removed with the capacity UI.
 *
 * Source: docs/phase2/team-resources-unified-ui-design.md §5; CR §8.3, §8.4; migration V043 (F).
 */

import { PROJECT_ROLE_VALUES } from "@/types/intake";

export interface RoleSection {
  /** Stable id — used for `aria-labelledby`, React keys, and test queries. */
  readonly id: string;
  /**
   * Section heading, plural where a section holds several people.
   *
   * Cosmetic and intentionally independent of {@link RoleSection.roleKey} — e.g. the `Executive`
   * role is displayed as "Account Executive". Change this freely; never mirror it into `roleKey`.
   */
  readonly label: string;
  /**
   * The `role_on_project` value this section holds, or `null` for the catch-all.
   *
   * `null` means "every assignment whose role matches no other section" — which includes the four
   * deprecated legacy values (`Developer`, `Junior Developer`, `Frontend Developer`,
   * `Backend Developer`) that V043 (G) seeded as `is_active = false`. Those rows must remain
   * VISIBLE on existing projects even though they are never offered in a picker (OQ-12).
   */
  readonly roleKey: (typeof PROJECT_ROLE_VALUES)[number] | null;
  /** Label of the section's add action and dialog title, e.g. "Add Engineer". */
  readonly addLabel: string;
  /** Shown when the section has no assignments. */
  readonly emptyLabel: string;
  /**
   * How people join this section from the Team tab (T-29 / T-31 — guarded team changes):
   *
   *   - `"none"`            no add action at all (Leadership, Account Executive — set at intake;
   *                          changed only via Swap).
   *   - `"assign-if-empty"` single-holder roles (SA, PM, Implementation Lead): the add action is
   *                          shown ONLY while nobody holds the role, so a project can still get its
   *                          first holder. Once held, the holder changes only via Swap.
   *   - `"confirm"`         multi-holder roles that legitimately grow mid-project (Engineers,
   *                          Other): the add action first asks {@link RoleSection.addConfirm}.
   */
  readonly addMode: "none" | "assign-if-empty" | "confirm";
  /** Confirmation copy for `addMode: "confirm"`. */
  readonly addConfirm?: {
    readonly title: string;
    readonly description: string;
  };
  /**
   * Rows may be removed outright (with confirmation). Only for the multi-holder sections that can
   * also be added to; every other row is read-only and changes via Swap.
   */
  readonly removable: boolean;
}

/**
 * The seven sections, in display order.
 *
 * Order is deliberate: governance roles first (Leadership, Account Executive), then the two named
 * delivery leads a project always has (SA, PM), then the delivery team led by the Implementation
 * Lead, then the catch-all last.
 *
 * 🚨 `label` / `addLabel` / `emptyLabel` ARE COSMETIC; `roleKey` IS THE WIRE VALUE.
 * They are deliberately decoupled — `Account Executive` and `Implementation Lead` are display
 * renames only, over the unchanged backend enum members `Executive` and
 * `Lead Implementation Engineer`. Renaming a `roleKey` to match a label would change what is
 * POSTed, break the Zod enum (400), and orphan every stored row holding the old value. Role
 * values that surface as text elsewhere in the UI go through `projectRoleLabel()` in
 * `@/types/intake` for the same reason. `id` is likewise stable and NOT a label — it backs
 * `aria-labelledby`, React keys, and test queries.
 *
 * ⚠️ `Lead Engineer` has NO section of its own. It is a LEGACY value kept active because DP-013
 * holds a live, legitimate `Lead Engineer` row (CR §8.3 BE-2) — renaming it would rewrite a live
 * row with no customer source for the new name being authoritative. `Lead Implementation Engineer`
 * is the canonical value and gets the section; existing `Lead Engineer` rows surface under
 * "Other" so they stay visible and removable. It is also NOT given the `Implementation Lead`
 * display label — two distinct stored values must stay distinguishable on screen.
 */
export const ROLE_SECTIONS: readonly RoleSection[] = [
  {
    id: "leadership",
    label: "Leadership",
    roleKey: "Leadership",
    addLabel: "Add Leadership",
    emptyLabel: "No leadership assigned",
    addMode: "none",
    removable: false,
  },
  {
    id: "executive",
    label: "Account Executive",
    roleKey: "Executive",
    addLabel: "Add Account Executive",
    emptyLabel: "No account executive assigned",
    addMode: "none",
    removable: false,
  },
  {
    id: "solution-architects",
    label: "Solution Architects",
    roleKey: "Solution Architect",
    addLabel: "Assign Solution Architect",
    emptyLabel: "No solution architect assigned",
    addMode: "assign-if-empty",
    removable: false,
  },
  {
    id: "project-managers",
    label: "Project Managers",
    roleKey: "Project Manager",
    addLabel: "Assign Project Manager",
    emptyLabel: "No project manager assigned",
    addMode: "assign-if-empty",
    removable: false,
  },
  {
    id: "lead-implementation-engineer",
    label: "Implementation Lead",
    roleKey: "Lead Implementation Engineer",
    addLabel: "Assign Implementation Lead",
    emptyLabel: "No implementation lead assigned",
    addMode: "assign-if-empty",
    removable: false,
  },
  {
    id: "engineers",
    label: "Engineers",
    roleKey: "Engineer",
    addLabel: "Add Engineer",
    emptyLabel: "No engineers assigned",
    addMode: "confirm",
    addConfirm: {
      title: "Add an engineer mid-project?",
      description: "They will get access to the project's tools.",
    },
    removable: true,
  },
  {
    id: "other",
    label: "Other",
    roleKey: null,
    addLabel: "Add Other",
    emptyLabel: "No other resources assigned",
    addMode: "confirm",
    addConfirm: {
      title: "Add a team member mid-project?",
      description: "They will get access to the project's tools.",
    },
    removable: true,
  },
];

/** Every `role_on_project` value that has a dedicated section. Drives the "Other" catch-all. */
export const SECTIONED_ROLE_KEYS: ReadonlySet<string> = new Set(
  ROLE_SECTIONS.flatMap<string>((s) => (s.roleKey === null ? [] : [s.roleKey])),
);

/**
 * Which section owns a given `role_on_project` value.
 *
 * Accepts `string` rather than `ProjectRole` on purpose — the value comes from the DATABASE, which
 * can still return one of the four deprecated legacy roles. Narrowing a read-path value to the
 * enum would be a type lie (see the warning in `packages/shared/constants/project-roles.ts`).
 * Anything unrecognised lands in "Other", which is the correct and safe default.
 */
export function sectionIdForRole(role: string | null | undefined): string {
  if (role && SECTIONED_ROLE_KEYS.has(role)) {
    const match = ROLE_SECTIONS.find((s) => s.roleKey === role);
    if (match) return match.id;
  }
  return "other";
}
